import React from 'react';
import { Container, Typography, Box, List, ListItem, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';

const URL_PATTERN = /https?:\/\/[^\s]+/g;

// Splits text into segments of plain text and URLs, rendering URLs as links.
const TextWithLinks: React.FC<{ text: string }> = ({ text }) => {
  const segments: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  URL_PATTERN.lastIndex = 0;
  while ((match = URL_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    segments.push(
      <Link key={match.index} href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </Link>
    );
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }

  return <>{segments}</>;
};

const BulletList: React.FC<{ items: string[] }> = ({ items }) => {
  const { t } = useTranslation();
  return (
    <List dense disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
      {items.map((item, i) => (
        <ListItem key={i} disableGutters sx={{ display: 'list-item' }}>
          <Typography variant="body2"><TextWithLinks text={t(item)} /></Typography>
        </ListItem>
      ))}
    </List>
  );
};

export const CreditsPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Container maxWidth="md" component="main" id="main-content">
        <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 5, mb: 7 }}>
        {t('Credits for Validated Questionnaires and Acoustic Tasks in the Bridge2AI-Voice Protocol')}
      </Typography>

      {/* Copyrights */}
      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        {t('Copyrights')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Adult ADHD Self-Report Scale (ASRS v1.1)')}</Typography>
        <Typography variant="body2">
          {t('Copyright © 2003 World Health Organization. Reprinted with permission of WHO. All rights reserved. \n© 1988 American Psychological Association (APA). Watson, D., Clark, L.A., & Tellegen, A.')}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Positive and Negative Affect Schedule (PANAS)')}</Typography>
        <Typography variant="body2">
          {t('© 1988 American Psychological Association (APA). Watson, D., Clark, L.A., & Tellegen, A.')}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('WHODAS 2.0')}</Typography>
        <Typography variant="body2">{t('Copyright © 2012 World Health Organization')}</Typography>
      </Box>

      {/* References */}
      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        {t('References')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Mental Health Assessments')}</Typography>
        <BulletList items={[
          'Kroenke K, Spitzer RL, Williams JB. The PHQ-9: validity of a brief depression severity measure. J Gen Intern Med. 2001;16(9):606-613. doi:10.1046/j.1525-1497.2001.016009606.x',
          'Spitzer RL, Kroenke K, Williams JBW, Löwe B. A brief measure for assessing generalized anxiety disorder: the GAD-7. Arch Intern Med. 2006;166(10):1092-1097. doi:10.1001/archinte.166.10.1092',
          'Kessler RC, Adler L, Ames M, et al. The World Health Organization Adult ADHD Self-Report Scale (ASRS): a short screening scale for use in the general population. Psychol Med. 2005;35(2):245-256. doi:10.1017/S0033291704002892',
          'Watson D, Clark LA, Tellegen A. Development and validation of brief measures of positive and negative affect: the PANAS scales. J Pers Soc Psychol. 1988;54(6):1063-1070. doi:10.1037/0022-3514.54.6.1063',
          'Üstün TB, Kostanjsek N, Chatterji S, Rehm J, eds. Measuring Health and Disability: Manual for WHO Disability Assessment Schedule (WHODAS 2.0). World Health Organization; 2010.',
          'American Psychiatric Association. Diagnostic and Statistical Manual of Mental Disorders. 5th ed, text rev. American Psychiatric Association Publishing; 2022.'
        ]} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Voice and Speech Assessments')}</Typography>
        <BulletList items={[
          'Jacobson BH, Johnson A, Grywalski C, et al. The Voice Handicap Index (VHI): development and validation. Am J Speech Lang Pathol. 1997;6(3):66-70. doi:10.1044/1058-0360.0603.66',
          'Rosen CA, Lee AS, Osborne J, Zullo T, Murry T. Development and validation of the Voice Handicap Index-10. Laryngoscope. 2004;114(9):1549-1556. doi:10.1097/00005537-200409000-00009',
          'Kempster GB, Gerratt BR, Verdolini Abbott K, Barkmeier-Kraemer J, Hillman RE. Consensus Auditory-Perceptual Evaluation of Voice: development of a standardized clinical protocol. Am J Speech Lang Pathol. 2009;18(2):124-132. doi:10.1044/1058-0360(2008/08-0017)'
        ]} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('Note: CAPE-V published under CC-BY 4.0 license. Supplemental materials: ')}
          <a href="https://authors.elsevier.com/sd/article/S0892-1997(25)00021-9" target="_blank" rel="noreferrer">https://authors.elsevier.com/sd/article/S0892-1997(25)00021-9</a>
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Respiratory Assessments')}</Typography>
        <BulletList items={[
          'Birring SS, Prudon B, Carr AJ, Singh SJ, Morgan MD, Pavord ID. Development of a symptom specific health status measure for patients with chronic cough: Leicester Cough Questionnaire (LCQ). Thorax. 2003;58(4):339-343. doi:10.1136/thorax.58.4.339',
          'Mahler DA, Weinberg DH, Wells CK, Feinstein AR. The measurement of dyspnea. Contents, interobserver agreement, and physiologic correlates of two new clinical indexes. Chest. 1984;85(6):751-758. doi:10.1378/chest.85.6.751',
          'Miller MR, Hankinson J, Brusasco V, et al. Standardisation of spirometry. Eur Respir J. 2005;26(2):319-338. doi:10.1183/09031936.05.00034805'
        ]} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Cognitive and Language Assessments')}</Typography>
        <BulletList items={[
          'Nasreddine ZS, Phillips NA, Bédirian V, et al. The Montreal Cognitive Assessment, MoCA: a brief screening tool for mild cognitive impairment. J Am Geriatr Soc. 2005;53(4):695-699. doi:10.1111/j.1532-5415.2005.53221.x',
          'Stroop JR. Studies of interference in serial verbal reactions. J Exp Psychol. 1935;18(6):643-662. doi:10.1037/h0054651',
          'Fletcher SG. Time-by-count measurement of diadochokinetic syllable rate. J Speech Hear Res. 1972;15(4):763-770. doi:10.1044/jshr.1504.763'
        ]} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Picture Description')}</Typography>
        <BulletList items={[
          'Berube S, Hillis AE, Faria AV, et al. Stealing cookies in the twenty-first century: measures of spoken narrative in healthy versus speakers with aphasia. Am J Speech Lang Pathol. 2019;28(1S):404-420. doi:10.1044/2018_AJSLP-17-0131',
          'Revised Cookie Theft picture used with permission from Dr. Argye E. Hillis, Johns Hopkins University School of Medicine. Originally published under CC-BY 4.0 license'
        ]} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Story Recall')}</Typography>
        <BulletList items={[
          'Systematic Analysis of Language Transcripts (SALT) Software LLC. Frog, Where Are You? Available from: https://www.saltsoftware.com'
        ]} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h3">{t('Reading Passages')}</Typography>
        <BulletList items={[
          'IEEE Subcommittee on Subjective Measurements. IEEE recommended practice for speech quality measurements. IEEE Trans Audio Electroacoust. 1969;17(3):225-246. doi:10.1109/TAU.1969.1162058'
        ]} />
      </Box>

      {/* Acknowledgments */}
      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
        {t('Acknowledgments')}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" component="h3">{t('Permissions obtained from:')}</Typography>
        <BulletList items={[
          'American Psychiatric Association (DSM-5 diagnostic criteria)',
          'Dr. Argye E. Hillis, Johns Hopkins University (Revised Cookie Theft picture)',
          'MAPI Research Trust (Voice Handicap Index-10)',
          'SALT Software LLC (Frog, Where Are You? story script)',
          'World Health Organization (ASRS v1.1, WHODAS 2.0)',
          'Open access tools used under CC-BY 4.0 licenses:',
          'CAPE-V (Consensus Auditory-Perceptual Evaluation of Voice)',
          'Revised Cookie Theft picture description task'
        ]} />
      </Box>
    </Container>
  );
};
